/**
 * The legal documents a user must accept, and which version of each counts.
 *
 * Two numbers per policy, and the gap between them is the whole mechanism:
 * `currentVersion` moves on every edit so the record stays exact, while
 * `minimumAcceptedVersion` moves only when Legal calls a change material. A
 * stored acceptance below the floor is stale, so raising the floor re-prompts
 * everyone who accepted under the old wording and raising only the version
 * ships a correction silently.
 *
 * `packages/api/src/policies.py` holds the same two numbers and is what actually
 * enforces them; this copy decides what the user is shown. `README.md` in this
 * directory is the procedure for changing either.
 */

import { parsePolicyDocument, type PolicyDocument } from "./parse";
import aiTransparencyPrivacyNoticeV1 from "./ai-transparency-privacy-notice/v1.md?raw";
import termsOfUseV1 from "./terms-of-use/v1.md?raw";

export const POLICY_IDS = [
  "ai-transparency-privacy-notice",
  "terms-of-use",
] as const;

export type PolicyId = (typeof POLICY_IDS)[number];

interface PolicyRegistration {
  currentVersion: number;
  minimumAcceptedVersion: number;
  /**
   * Only the current version is bundled. Superseded versions stay in the repo
   * as the record of what a stored acceptance refers to, but shipping every one
   * of them would grow the bundle for every edit ever made.
   */
  raw: string;
}

const REGISTRY: Record<PolicyId, PolicyRegistration> = {
  "ai-transparency-privacy-notice": {
    currentVersion: 1,
    minimumAcceptedVersion: 1,
    raw: aiTransparencyPrivacyNoticeV1,
  },
  "terms-of-use": {
    currentVersion: 1,
    minimumAcceptedVersion: 1,
    raw: termsOfUseV1,
  },
};

export interface Policy {
  id: PolicyId;
  currentVersion: number;
  minimumAcceptedVersion: number;
  document: PolicyDocument;
}

/**
 * Parsed at module load, so a malformed document fails the import rather than
 * rendering a consent dialog with a blank tick box label.
 */
export const POLICIES: readonly Policy[] = POLICY_IDS.map((id) => {
  const registration = REGISTRY[id];
  return {
    id,
    currentVersion: registration.currentVersion,
    minimumAcceptedVersion: registration.minimumAcceptedVersion,
    document: parsePolicyDocument(registration.raw),
  };
});

export function findPolicy(id: string): Policy | undefined {
  return POLICIES.find((policy) => policy.id === id);
}
